import { useState, useEffect } from 'react';
import { useOffline } from '../../context/OfflineContext';
import { useToast } from '../../context/ToastContext';
import { budgetsApi, tripsApi } from '../../services/api';
import { getBudgetSummary, setBudget } from '../../services/syncService';
import { BUDGET_CATEGORIES_ORDERED, formatCurrency, formatPercent, getCategoryStyle } from '../../utils/categories';
import { getCategoryIcon } from '../UI/Icons';
import BudgetSkeleton from './BudgetSkeleton';
import { CaretDown, CalendarBlank, MapPin } from '@phosphor-icons/react';

export default function BudgetDashboard() {
  const { isOnline, isReady, updatePendingCount } = useOffline();
  const toast = useToast();
  
  // Data state
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [years, setYears] = useState([]);
  const [trips, setTrips] = useState([]);
  
  // Filter state
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedTripId, setSelectedTripId] = useState(null); // null = Annual view
  
  // Editing state
  const [editingCategory, setEditingCategory] = useState(null);
  const [budgetAmount, setBudgetAmount] = useState('');

  // Load years and trips on mount
  useEffect(() => {
    if (isReady && isOnline) {
      loadYearsAndTrips();
    }
  }, [isReady, isOnline]);

  // Load summary when filters change
  useEffect(() => {
    if (isReady) {
      loadSummary();
    }
  }, [isReady, isOnline, selectedYear, selectedTripId]);

  async function loadYearsAndTrips() {
    try {
      const [yearsData, tripsData] = await Promise.all([
        budgetsApi.getYears(),
        tripsApi.list(),
      ]);
      setYears(yearsData.years || []);
      setTrips(tripsData || []);
    } catch (err) {
      console.error('Failed to load years/trips:', err);
    }
  }

  async function loadSummary() {
    try {
      setLoading(true);
      const params = selectedTripId 
        ? { trip_id: selectedTripId }
        : { year: selectedYear };
      
      if (isOnline) {
        const data = await budgetsApi.getSummary(params);
        setSummary(data);
      } else {
        // Offline fallback - use sync service without params
        const data = await getBudgetSummary(false);
        setSummary(data);
      }
    } catch (err) {
      console.error('Failed to load budget summary:', err);
    } finally {
      setLoading(false);
    }
  }

  async function handleSetBudget(category) {
    if (!budgetAmount || isNaN(parseFloat(budgetAmount))) return;
    
    try {
      const budgetData = {
        category,
        amount: parseFloat(budgetAmount),
        currency: 'EUR',
      };
      
      // Add year or trip_id based on current view
      if (selectedTripId) {
        budgetData.trip_id = selectedTripId;
      } else {
        budgetData.year = selectedYear;
      }
      
      await setBudget(budgetData, isOnline);
      setEditingCategory(null);
      setBudgetAmount('');
      if (!isOnline) updatePendingCount();
      toast.success('Budget updated');
      loadSummary();
    } catch (err) {
      console.error('Failed to set budget:', err);
      toast.error('Failed to update budget');
    }
  }

  // Get trips that have events in the selected year
  const tripsForYear = trips.filter(trip => {
    if (!trip.first_event && !trip.last_event) return true; // Show trips without events
    const tripStart = trip.first_event ? new Date(trip.first_event).getFullYear() : null;
    const tripEnd = trip.last_event ? new Date(trip.last_event).getFullYear() : null;
    return (tripStart && tripStart <= selectedYear && tripEnd >= selectedYear) ||
           (!tripStart && !tripEnd);
  });

  // Get the selected trip name for display
  const selectedTrip = trips.find(t => t.id === selectedTripId);

  if (loading && !summary) {
    return <BudgetSkeleton />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-display font-semibold" style={{ color: 'var(--color-text-primary)' }}>
            Budget Overview
          </h2>
          <p style={{ color: 'var(--color-text-muted)' }} className="mt-1">
            {selectedTripId 
              ? `Trip budget for ${selectedTrip?.name || 'Unknown trip'}`
              : `Annual budget for ${selectedYear}`
            }
          </p>
        </div>

        {/* Filters */}
        {isOnline && (
          <div className="flex flex-wrap items-center gap-3">
            {/* Year Selector */}
            <div className="relative">
              <div className="flex items-center gap-2">
                <CalendarBlank size={16} style={{ color: 'var(--color-text-muted)' }} />
                <select
                  value={selectedYear}
                  onChange={(e) => {
                    setSelectedYear(parseInt(e.target.value));
                    setSelectedTripId(null); // Reset to annual view when year changes
                  }}
                  className="input pr-8 py-1.5 text-sm min-w-[100px]"
                  style={{ appearance: 'none' }}
                >
                  {years.map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
                <CaretDown 
                  size={14} 
                  className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none"
                  style={{ color: 'var(--color-text-muted)' }}
                />
              </div>
            </div>

            {/* Trip Toggle */}
            <div className="relative">
              <div className="flex items-center gap-2">
                <MapPin size={16} style={{ color: 'var(--color-text-muted)' }} />
                <select
                  value={selectedTripId || ''}
                  onChange={(e) => setSelectedTripId(e.target.value || null)}
                  className="input pr-8 py-1.5 text-sm min-w-[140px]"
                  style={{ appearance: 'none' }}
                >
                  <option value="">Annual ({selectedYear})</option>
                  {tripsForYear.map(trip => (
                    <option key={trip.id} value={trip.id}>
                      {trip.name}
                    </option>
                  ))}
                </select>
                <CaretDown 
                  size={14} 
                  className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none"
                  style={{ color: 'var(--color-text-muted)' }}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Total Summary Card */}
      <div className="card">
        <div className="card-body">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            <div>
              <div className="text-sm font-medium" style={{ color: 'var(--color-text-muted)' }}>Total Budget</div>
              <div className="text-2xl md:text-3xl font-display font-bold mt-1" style={{ color: 'var(--color-text-primary)' }}>
                {formatCurrency(summary?.totals?.budget || 0)}
              </div>
            </div>
            <div>
              <div className="text-sm font-medium" style={{ color: 'var(--color-text-muted)' }}>Planned</div>
              <div className="text-xl md:text-2xl font-display font-bold mt-1" style={{ color: 'var(--color-category-6)' }}>
                {formatCurrency(summary?.totals?.planned || 0)}
              </div>
              <div className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
                {formatPercent(summary?.totals?.planned_vs_budget_pct || 0)} of budget
              </div>
            </div>
            <div>
              <div className="text-sm font-medium" style={{ color: 'var(--color-text-muted)' }}>Actual</div>
              <div className="text-xl md:text-2xl font-display font-bold mt-1" style={{ color: 'var(--color-status-success)' }}>
                {formatCurrency(summary?.totals?.actual || 0)}
              </div>
              <div className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
                {formatPercent(summary?.totals?.actual_vs_budget_pct || 0)} of budget
              </div>
            </div>
            <div>
              <div className="text-sm font-medium" style={{ color: 'var(--color-text-muted)' }}>Remaining</div>
              <div 
                className="text-xl md:text-2xl font-display font-bold mt-1"
                style={{ color: (summary?.totals?.remaining || 0) < 0 ? 'var(--color-status-error)' : 'var(--color-text-primary)' }}
              >
                {formatCurrency(summary?.totals?.remaining || 0)}
              </div>
              <div className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
                {formatPercent(100 - (summary?.totals?.total_spent_pct || 0))} left
              </div>
            </div>
          </div>

          {/* Overall Progress Bar */}
          <div className="mt-6">
            <div className="flex items-center justify-between text-sm mb-2">
              <span style={{ color: 'var(--color-text-muted)' }}>Overall Spending</span>
              <span className="font-medium" style={{ color: 'var(--color-text-primary)' }}>{formatPercent(summary?.totals?.total_spent_pct || 0)}</span>
            </div>
            <div 
              className="h-3 rounded-full overflow-hidden"
              style={{ backgroundColor: 'var(--color-bg-surface)' }}
            >
              <div className="h-full flex">
                <div 
                  className="transition-all duration-500"
                  style={{ 
                    width: `${Math.min(summary?.totals?.planned_vs_budget_pct || 0, 100)}%`,
                    backgroundColor: 'var(--color-category-6)',
                  }}
                />
                <div 
                  className="transition-all duration-500"
                  style={{ 
                    width: `${Math.min(summary?.totals?.actual_vs_budget_pct || 0, 100 - (summary?.totals?.planned_vs_budget_pct || 0))}%`,
                    backgroundColor: 'var(--color-status-success)',
                  }}
                />
              </div>
            </div>
            <div className="flex items-center gap-4 mt-2 text-xs">
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 rounded" style={{ backgroundColor: 'var(--color-category-6)' }} />
                <span style={{ color: 'var(--color-text-muted)' }}>Planned</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 rounded" style={{ backgroundColor: 'var(--color-status-success)' }} />
                <span style={{ color: 'var(--color-text-muted)' }}>Actual</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Category Breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {BUDGET_CATEGORIES_ORDERED.map(cat => {
          const catData = summary?.categories?.[cat.value] || {};
          const isEditing = editingCategory === cat.value;
          const categoryStyle = getCategoryStyle(cat.colorVar);

          return (
            <div key={cat.value} className="card">
              <div className="card-body">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-10 h-10 rounded-md flex items-center justify-center"
                      style={categoryStyle}
                    >
                      {getCategoryIcon(cat.value, { size: 24 })}
                    </div>
                    <div>
                      <h3 className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>{cat.label}</h3>
                      <div className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
                        Budget: {formatCurrency(catData.budget || 0)}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setEditingCategory(isEditing ? null : cat.value);
                      setBudgetAmount(catData.budget?.toString() || '');
                    }}
                    className="p-1 transition-colors"
                    style={{ color: 'var(--color-text-muted)' }}
                    aria-label={`Edit ${cat.label} budget`}
                  >
                    <EditIcon className="w-4 h-4" />
                  </button>
                </div>

                {isEditing ? (
                  <div className="flex items-center gap-2 mb-4">
                    <input
                      type="number"
                      value={budgetAmount}
                      onChange={(e) => setBudgetAmount(e.target.value)}
                      className="input flex-1"
                      placeholder="Budget amount"
                      min="0"
                      step="0.01"
                    />
                    <button
                      onClick={() => handleSetBudget(cat.value)}
                      className="btn-primary btn-sm"
                    >
                      Save
                    </button>
                  </div>
                ) : null}

                {/* Progress bar */}
                <div className="mb-3">
                  <div 
                    className="h-2 rounded-full overflow-hidden"
                    style={{ backgroundColor: 'var(--color-bg-surface)' }}
                  >
                    <div className="h-full flex">
                      <div 
                        className="transition-all duration-500"
                        style={{ 
                          width: `${Math.min(catData.planned_vs_budget_pct || 0, 100)}%`,
                          backgroundColor: 'var(--color-category-6)',
                        }}
                      />
                      <div 
                        className="transition-all duration-500"
                        style={{ 
                          width: `${Math.min(catData.actual_vs_budget_pct || 0, 100 - (catData.planned_vs_budget_pct || 0))}%`,
                          backgroundColor: 'var(--color-status-success)',
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div>
                    <div className="text-xs" style={{ color: 'var(--color-text-muted)' }}>Planned</div>
                    <div className="font-semibold" style={{ color: 'var(--color-category-6)' }}>
                      {formatCurrency(catData.planned || 0)}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs" style={{ color: 'var(--color-text-muted)' }}>Actual</div>
                    <div className="font-semibold" style={{ color: 'var(--color-status-success)' }}>
                      {formatCurrency(catData.actual || 0)}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs" style={{ color: 'var(--color-text-muted)' }}>Remaining</div>
                    <div 
                      className="font-semibold"
                      style={{ 
                        color: (catData.remaining_from_budget || 0) < 0 
                          ? 'var(--color-status-error)' 
                          : 'var(--color-text-secondary)' 
                      }}
                    >
                      {formatCurrency(catData.remaining_from_budget || 0)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function EditIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
    </svg>
  );
}
